// @ts-check
import axios from 'axios';
import {
  ASSIGN_PROJECTS_TO_ROLES_URL,
  CREATE_PROJECT_BINDINGS,
  EDIT_PROJECT,
  GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
  REMOVE_PROJECT_BINDING_URL,
  UPDATE_USER,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { is_number, response_rows, to_array } from './tenant_configuration_editor_normalisers';
import { normalize_project_assignment } from './tenant_configuration_editor_role_helpers';
import {
  build_contact_update_payload,
  build_project_update_payload,
  split_name as split_editor_name
} from './tenant_configuration_editor_payloads';

/**
 * @typedef {import('./tenant_configuration_editor_types').TenantConfigurationEditorInstance} TenantConfigurationEditorInstance
 */

/** @this {TenantConfigurationEditorInstance} @returns {{first_name: string, last_name: string}} */
export function split_name() {
  return split_editor_name(this);
}

/** @this {TenantConfigurationEditorInstance} @returns {Promise<boolean>} */
export async function save_details() {
  const name = this.split_name();
  if (!this.selected_project_id || !this.form.organisation_name.trim()) return false;

  await axios.post(EDIT_PROJECT, build_project_update_payload(this), get_request_config());
  const contact_payload = build_contact_update_payload(this, name);
  if (contact_payload) {
    await axios.post(UPDATE_USER, contact_payload, get_request_config());
  }

  return true;
}

/** @this {TenantConfigurationEditorInstance} @returns {Promise<void>} */
export async function save_domains() {
  const selected_config_ids = new Set(
    this.domain_options
      .filter((domain) => this.form.domains.includes(domain.key))
      .map((domain) => domain.config_id)
  );
  await Promise.all(this.project_bindings.filter((binding) =>
    binding.is_active && !selected_config_ids.has(binding.entity_config)
  ).map((binding) =>
    axios.post(REMOVE_PROJECT_BINDING_URL, { binding_id: binding.id }, get_request_config())
  ));
  const active_config_ids = new Set(
    this.project_bindings
      .filter((binding) => binding.is_active)
      .map((binding) => binding.entity_config)
  );
  await Promise.all([...selected_config_ids].filter((id) => !active_config_ids.has(id)).map((entity_config_id) =>
    axios.post(CREATE_PROJECT_BINDINGS, { entity_config_id, project_id: this.selected_project_id }, get_request_config())
  ));
}

/** @this {TenantConfigurationEditorInstance} @returns {Promise<void>} */
export async function save_roles() {
  const selected = new Set(this.form.roles.map(Number));
  await Promise.all(this.role_options.map(async (role) => {
    const response = await axios.post(
      GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
      { role_id: role.key },
      get_request_config()
    );
    const ids = to_array(response_rows(response), normalize_project_assignment)
      .map((assignment) => assignment.project)
      .filter(is_number)
      .filter((id) => id !== this.selected_project_id);
    if (selected.has(role.key)) ids.push(this.selected_project_id);
    await axios.post(
      ASSIGN_PROJECTS_TO_ROLES_URL,
      { role_id: role.key, project_ids: [...new Set(ids)] },
      get_request_config()
    );
  }));
}

export const tenant_configuration_editor_save_methods = {
  split_name,
  save_details,
  save_domains,
  save_roles
};
